import { useEffect, useState } from "react";
import { useConsolidationContainerChanges } from "@/hooks/useConsolidation";
import { computeHtmlDiff } from "@/lib/htmlDiff";
import { toAmendmentReviewDiffHtml } from "@/lib/containerAmendmentDiff";
import { logger } from "@/lib/logger";
import type {
  ContainerChangeDetail,
  GetContainerChangeDetailParams,
} from "@/models/consolidation";

/**
 * Resolves the cumulative before/after container HTML across every amendment
 * applied to the container: the earliest change's "before" and the latest
 * change's "after". Returns null when either edge is missing so the caller can
 * fall back to "cannot highlight".
 */
const resolveCumulativeSlice = (
  changes: ContainerChangeDetail[] | undefined
): { before: string; after: string } | null => {
  if (!changes || changes.length === 0) return null;
  const before = changes[0]?.containerHtmlBefore?.trim();
  const after = changes[changes.length - 1]?.containerHtmlAfter?.trim();
  if (!before || !after) return null;
  return { before, after };
};

/**
 * A container with no "before" state but a "after" state was inserted whole
 * (e.g. an inserted group heading or paragraph). There is no precise diff to
 * highlight — the entire element is new — so this is a distinct, expected case
 * rather than a diff failure.
 */
const isFullInsertionSlice = (
  changes: ContainerChangeDetail[] | undefined
): boolean => {
  if (!changes || changes.length === 0) return false;
  const before = changes[0]?.containerHtmlBefore?.trim();
  const after = changes[changes.length - 1]?.containerHtmlAfter?.trim();
  return !before && Boolean(after);
};

/**
 * A container with a "before" state but no "after" state was deleted whole (e.g.
 * a repealed group heading or chapter). Like a full insertion, there is no
 * precise diff to highlight — the whole element was removed — so this is a
 * distinct, expected case rather than a diff failure.
 */
const isFullDeletionSlice = (
  changes: ContainerChangeDetail[] | undefined
): boolean => {
  if (!changes || changes.length === 0) return false;
  const before = changes[0]?.containerHtmlBefore?.trim();
  const after = changes[changes.length - 1]?.containerHtmlAfter?.trim();
  return Boolean(before) && !after;
};

export const useContainerAmendmentDiff = (
  params: GetContainerChangeDetailParams | null,
  enabled: boolean
) => {
  const query = useConsolidationContainerChanges(enabled ? params : null);
  const [diffHtml, setDiffHtml] = useState<string | null>(null);
  const [isComputingDiff, setIsComputingDiff] = useState(false);
  const [diffComputeError, setDiffComputeError] = useState(false);

  const cumulativeSlice = resolveCumulativeSlice(query.data);
  const canComputeDiff = cumulativeSlice !== null;
  const isFullInsertion = isFullInsertionSlice(query.data);
  const isFullDeletion = isFullDeletionSlice(query.data);

  useEffect(() => {
    if (!enabled || !query.data) {
      setDiffHtml(null);
      setIsComputingDiff(false);
      setDiffComputeError(false);
      return;
    }

    const slice = resolveCumulativeSlice(query.data);
    if (!slice) {
      setDiffHtml(null);
      setIsComputingDiff(false);
      setDiffComputeError(false);
      return;
    }

    let cancelled = false;
    const containerId = query.data[0]?.containerId;
    setIsComputingDiff(true);
    setDiffComputeError(false);
    setDiffHtml(null);

    computeHtmlDiff(slice.before, slice.after)
      .then((rawDiff) => {
        if (cancelled) return;
        setDiffHtml(toAmendmentReviewDiffHtml(rawDiff));
      })
      .catch((error) => {
        if (cancelled) return;
        logger.error("Failed to compute the container amendment diff", {
          operation: "computeContainerAmendmentDiff",
          error,
          containerId,
        });
        setDiffComputeError(true);
      })
      .finally(() => {
        if (!cancelled) setIsComputingDiff(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, query.data]);

  const cannotHighlightDiff =
    enabled &&
    !query.isLoading &&
    Boolean(query.data && query.data.length > 0) &&
    !canComputeDiff &&
    !isFullInsertion &&
    !isFullDeletion &&
    !diffComputeError;

  return {
    ...query,
    diffHtml,
    isComputingDiff,
    diffComputeError,
    cannotHighlightDiff,
    isFullInsertion,
    isFullDeletion,
  };
};
